﻿
import { ProjectionLike, transform, transformExtent } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import { getDistance } from 'ol/sphere';

import { Projections } from '../models/Projections';

export class Gonio {

    private static readonly _180dmx = transform([180, 0], "EPSG:4326", "EPSG:3857")[0];
    private static readonly _360dmx = 2 * transform([180, 0], "EPSG:4326", "EPSG:3857")[0];

    private static readonly _rad2Deg = 360 / (2 * Math.PI);
    private static readonly _deg2Rad = (2 * Math.PI) / 360;

    private static readonly _halfPI = Math.PI / 2.0;
    private static readonly _twoPI = Math.PI * 2.0;

  
    /**
     * Transforms degree to radiant
     * @param deg
     */
    public static degToRad(deg: number) {
        return deg * Gonio._deg2Rad;
    }
    
    /**
     * Transforms radiant to degree
     * @param rad
     */
    public static radToDeg(rad: number) {
        return rad * Gonio._rad2Deg;
    }

    public static metToDeg(coord: Coordinate, between180To180 = true) {
        var res = transform(coord, "EPSG:3857", "EPSG:4326");
        return between180To180 ? res.map(c => Gonio.angle180To180(c)) : res;
    }

    public static degToMet(coord: Coordinate) {
        return transform(coord, "EPSG:4326", "EPSG:3857");
    }

    public static metToRad(coord: Coordinate) {
        var deg = Gonio.metToDeg(coord);
        return [Gonio.degToRad(deg[0]), Gonio.degToRad(deg[1])];
    }

    public static radToMet(coord: Coordinate) {
        var deg = [Gonio.radToDeg(coord[0]), Gonio.radToDeg(coord[1])];
        return Gonio.degToMet(deg);
    }
   
    /**
     * Reduces angle to be between 0 and 360 degrees
     * @param deg
     */
    public static angle0To360(deg: number) {
        if (deg <= 360 && deg >= 0)
            return deg;
        deg = deg % 360.0;
        return deg < 0.0 ? deg + 360.0 : deg;
    }
    
    /**
     * Reduces angle to be between -180 and 180 degrees
     * @param deg
     */
    public static angle180To180(deg: number) {
        if (deg <= 180 && deg >= -180)
            return deg;
        deg = Gonio.angle0To360(deg);
        return deg >= 180.0 ? deg - 360.0 : deg;
    }
   
    /**
     * Reduces angle to be between 0 and 2PI degrees
     * @param rad
     */
    public static angle0To2PI(rad: number) {
        while (rad < 0) rad += Gonio._twoPI;
        while (rad > Gonio._twoPI) rad -= Gonio._twoPI;
        return rad;
    }
   
    /**
     * Reduces angle to be between -PI and PI degrees
     * @param rad
     */
    public static anglePIToPI(rad: number) {
        rad = Gonio.angle0To2PI(rad);
        return rad >= Math.PI ? rad - Gonio._twoPI : rad;
    }

    public static getPointResolution(resolution: number, point: Coordinate) {
        const p = this.metToDeg(point);
        const coslat = Math.cos(Math.abs(this.degToRad(p[1])));
        return resolution * coslat;
    }

    public static getZoomFromResolution(resolution: number) {
        return Math.log((40075008 / 256) / resolution) / Math.log(2);
    }

    public static getResolutionFromZoom(zoom: number) {
        return (40075008 / 256) / (Math.pow(2, zoom));
    }

    public static nearestZoomResolution(resolution: number): number {
        return this.getResolutionFromZoom(Math.round(this.getZoomFromResolution(resolution)));
    }

    public static getSnappedAngle(angle: number, snapSegments: number) {
        return Math.round(snapSegments * angle / 360) * 360 / snapSegments;
    }
 
    /**
     * Calculates the difference in angle between two angles
     * @param degA
     * @param degB
     */
    public static deltaBearing(degA: number, degB: number) {
        var delta = degA - degB;

        if (delta > +180.0) delta -= 360.0;
        else if (delta < -180.0) delta += 360.0;
        return delta;
    }

    public static deltaRad(degA: number, degB: number) {
        var delta = degA - degB;

        if (delta > +Math.PI) delta -= Math.PI * 2;
        else if (delta < -Math.PI) delta += Math.PI * 2;
        return delta;
    }    

    /**
     * Calculate distance in meters over greater circle
     * @param coordinate0
     * @param coordinate1
     * @param projection
     */
    public static distanceGC(coordinate0: Coordinate, coordinate1: Coordinate, projection: ProjectionLike = Projections.WebMercator) {
        let from = transform(coordinate0, projection, Projections.WGS);
        let to = transform(coordinate1, projection, Projections.WGS);
        return getDistance(from, to);
    }

    /**
     * Calculate distance in meters over rhumb line
     * @param coordinate0
     * @param coordinate1
     * @param projection
     */
    public static distanceRL(coordinate0: Coordinate, coordinate1: Coordinate, projection: ProjectionLike = Projections.WebMercator) {        
        let [x1, y1] = transform(coordinate0, projection, Projections.WebMercator);
        let [x2, y2] = transform(coordinate1, projection, Projections.WebMercator);        
        let x = x1 - x2;
        let y = y1 - y2;
        return Math.sqrt(Math.sqrt(x) + Math.sqrt(y));
    }

    /**
     * Calculate bearing in degrees over greater circle
     * @param coordinate0
     * @param coordinate1
     * @param projection
     */
    public static bearingGC(coordinate0: Coordinate, coordinate1: Coordinate, projection: ProjectionLike = Projections.WebMercator) {
        let coordA = transform(coordinate0, projection, Projections.WebMercator);
        let coordB = transform(coordinate1, projection, Projections.WebMercator);

        var radA = Gonio.metToRad(coordA);
        var radB = Gonio.metToRad(coordB);

        var sinLatA = Math.sin(radA[1]);
        var cosLatA = Math.cos(radA[1]);

        var lonAB = Gonio.anglePIToPI(radB[0] - radA[0]);
        var cosLonAB = Math.cos(lonAB);

        var tanLatB = Math.tan(radB[1]);
        var sinLonAB = Math.sin(lonAB);        
        
        // 	Calculate the noemer
        var temp = (cosLatA * tanLatB) - (sinLatA * cosLonAB);
        // Calculate dKA, a temporary bearing
        var bearing = Math.atan2(sinLonAB, temp);

        // Now actually calculate the Bearing
        if (temp == 0.0 && lonAB > 0) bearing = Gonio._halfPI;
        else if (temp == 0.0 && lonAB < 0) bearing = Gonio._halfPI + Math.PI;

        return Gonio.angle0To360(Gonio.radToDeg(bearing));               
    }

    /**
     * Calculate bearing in degrees over rhumb line
     * @param coordinate0
     * @param coordinate1
     * @param projection
     */
    public static bearingRL(coordinate0: Coordinate, coordinate1: Coordinate, projection: ProjectionLike = Projections.WebMercator) {
        let coordA = transform(coordinate0, projection, Projections.WebMercator);
        let coordB = transform(coordinate1, projection, Projections.WebMercator);

        const dx = coordA[0] - coordB[0];
        const dy = coordA[1] - coordB[1];
        const bearing = Math.atan2(dy, dx);

        return Gonio.angle0To360(Gonio.radToDeg(bearing));
    }

    ///// <summary>
    /////     Calculates area of a polygon, returns value in square meters
    ///// </summary>
    ///// <param name="coords">array of coordinates</param>
    //public static calcPolygonArea(coords) {
    //    if (coords.length < 3) return 0;

    //    var first = coords[0];
    //    var last = coords[coords.length - 1];
    //    if (first[0] != last[0] || first[1] != last[1])
    //        coords.push(first);

    //    var cMet1 = coords[0];
    //    var cDeg1 = Gonio.metToDeg(cMet1);
    //    var ltRad1 = Gonio.degToRad(cDeg1[1]);
    //    var cMet2, cDeg2, ltRad2;

    //    var area = 0;
    //    for (var i = 0; i < coords.length - 1; i++) {
    //        cMet2 = coords[i + 1];
    //        cDeg2 = Gonio.metToDeg(cMet2);
    //        ltRad2 = Gonio.degToRad(cDeg2[1]);

    //        area += Gonio._deg2Rad * (cDeg2[0] - cDeg1[0]) * (2 + Math.sin(ltRad2) + Math.sin(ltRad1));

    //        cMet1 = cMet2.slice();
    //        cDeg1 = cDeg2.slice();
    //        ltRad1 = ltRad2;
    //    }
    //    return Math.abs(area * 6378137 * 6378137 / 2);
    //}   

    //public static positionFromDistanceBearing(coord, bearing, dist) {
    //    var rad = Gonio.metToRad(coord);
    //    var lat = rad[1];
    //    var lon = rad[0];

    //    bearing = Gonio.degToRad(bearing);
    //    dist = Gonio.degToRad(dist / 60.0);

    //    var sinLat = Math.sin(lat);
    //    var cosLat = Math.cos(lat);
    //    var sinDist = Math.sin(dist);
    //    var cosDist = Math.cos(dist);

    //    var lat2 = Math.asin(sinLat * cosDist + cosLat * sinDist * Math.cos(bearing));
    //    var lon2 = lon + Math.atan2(Math.sin(bearing) * sinDist * cosLat, cosDist - sinLat * Math.sin(lat2));        

    //    return Gonio.radToMet([lon2, lat2]);
    //}


    //// Interpolate between two coordinates using GC
    //public static interpolate(coord1, coord2, fraction, distanceBearing = null)
    //{
    //    var unwrap = Gonio.unWrapSlice([coord1, coord2]);
    //    coord1 = unwrap.coords[0];
    //    coord2 = unwrap.coords[1];

    //    if ((coord1[0] === coord2[0] && coord1[1] === coord2[1]) || fraction == 0)
    //        return coord1;

    //    if (fraction == 1)
    //        return coord2;

    //    distanceBearing = distanceBearing || this.calcRangeBearingGC(coord1, coord2);
    //    var coord = this.positionFromDistanceBearing(coord1, distanceBearing.bearing, distanceBearing.distance * fraction);        

    //    if (isNaN(coord[0]) || isNaN(coord[1])) {
    //        console.error("Error occured while interpolating between two positions!");
    //        return coord1;
    //    }
    //    return Gonio.unWrapSlice([coord1, coord]).coords[1];
    //}

    //// Subdivide an array off coordinates using GC   
    //public static subdivide(coords, subdivisions, centerCoord?)
    //{       
    //    Gonio.setBetweenDateLines(coords);

    //    if (centerCoord) {
    //        Gonio.setBetweenDateLines(centerCoord);
    //        var unwrap = Gonio.unWrapSlice([centerCoord, coords[0]]);
    //        coords[0] = unwrap.coords[1];
    //    }  

    //    var lastCoord = coords[0];
    //    var result = [lastCoord];
    //    var distanceBearing, thisCoord;

    //    for (var i = 1; i < coords.length; i++) {
    //        thisCoord = coords[i];
    //        distanceBearing = Gonio.calcRangeBearingGC(lastCoord, thisCoord);
    //        for (var j = 0; j < (subdivisions + 1); j++) {
    //            result.push(this.interpolate(lastCoord, thisCoord, j / subdivisions, distanceBearing));
    //        }
    //        lastCoord = result[result.length - 1];
    //    }

    //    return result;       
    //}

    //public static getWrappedCoordinates(point: Coordinate, extent = Utilities.mapExtent)
    //{
    //    if (!point)
    //        return [];

    //    if (olExtent.isEmpty(extent))
    //        return [point];

    //    point[0] = Math.min(extent[2], point[0]);
    //    point[0] = Math.max(extent[0], point[0]);

    //    var coords: Coordinate[] = [];
    //    var range = Gonio.getExtentWrapRange_(extent);
    //    for (var i = range[0]; i <= range[1]; i++)          
    //        coords.push([point[0] + Gonio._360dmx * i, point[1]]);        
    //    return coords;
    //}    

    //private static getExtentWrapRange_(extent: olExtent.Extent) {
    //    return [
    //        Math.floor(extent[0] / Gonio._360dmx + 0.5),
    //        Math.floor(extent[2] / Gonio._360dmx + 0.5)
    //    ]
    //}

    public static setBetweenDateLines(points: any[]) {
        if (!points || !points.length)
            return;

        if (!(points[0] instanceof Array))
            points = [points];

        for (var i = 0; i < points.length; i++) {
            if (points[i][0] instanceof Array)
                Gonio.setBetweenDateLines(points[i]);
            else {
                if (points[i][0] > Gonio._180dmx) {
                    while (points[i][0] > Gonio._180dmx)
                        points[i][0] -= Gonio._360dmx;
                }
                else if (points[i][0] < -Gonio._180dmx) {
                    while (points[i][0] < -Gonio._180dmx)
                        points[i][0] += Gonio._360dmx;
                }
            }
        }
    }

    //    public static unWrapSlice(points, isClosed = false, firstPoint = points[0]) {
    //        var coords = Utilities.cloneCoordinates(points);
    //        var result = Gonio.unWrapInline(coords, isClosed, firstPoint);
    //        result.coords = coords;
    //        return result;
    //    }

    //    public static unWrapInline(points: any[], isClosed = false, firstPoint = points[0]) {

    //        var result = {
    //            failed: false,
    //            changed: false,
    //            coords: null
    //        }

    //        if (!points.length)
    //            return result;       

    //        if (points[0] instanceof Array == false)
    //            points = [points];

    //        var add = 0;        
    //        var last_x = firstPoint[0];        

    //        var tryUnWrap = (point, value) => {
    //            if (!value)
    //                return;

    //            var test = point[0] + value;
    //            if (test > Utilities.mapExtent[0] && test < Utilities.mapExtent[2]) {
    //                point[0] = test;
    //                result.changed = true;
    //                return value;
    //            }
    //            result.failed = true;
    //            return 0;
    //        }

    //        var unWrap = (point) => {
    //            var test, r;
    //            if (point[0] instanceof Array) {
    //                r = Gonio.unWrapInline(point, isClosed);
    //                for (var key in r) {
    //                    result[key] = r[key] || result[key];
    //                }
    //            }               
    //            else {
    //                add = tryUnWrap(point, add);               
    //                var dx = point[0] - last_x;
    //                if (dx > Gonio._180dmx) {
    //                    while ((point[0] - last_x) > Gonio._180dmx) {
    //                        test = tryUnWrap(point, -Gonio._360dmx);
    //                        add += test;
    //                        if (!test) break;
    //                    }                    
    //                }
    //                else if (dx < -Gonio._180dmx) {
    //                    while ((point[0] - last_x) < -Gonio._180dmx) {
    //                        test = tryUnWrap(point, Gonio._360dmx);
    //                        add += test;
    //                        if (!test) break;                        
    //                    }
    //                }
    //                last_x = point[0];
    //            }
    //        }

    //        points.forEach(unWrap);
    //        if (isClosed)
    //            unWrap(points[0]);

    //        return result;
    //    }

    //    public static splitLineInSegments(points) {       
    //        if (!points.length || (points[0] instanceof Array == false))
    //            return [];
    //        if (points.length == 1 && (points[0][0] instanceof Array == false))
    //            return [[points[0].slice(), points[0].slice()]];

    //        var result = [];
    //        for (var i = 0; i < points.length; i++) {
    //            if (points[i][0] instanceof Array)
    //                result = result.concat(Gonio.splitLineInSegments(points[i]));
    //            else if (i > 0)
    //                result.push([points[i - 1].slice(), points[i].slice()]);
    //        }

    //        return result;
    //    }

    //    public static getClosestPixel(line: Pixel[], pixel: Pixel) {

    //        var pixel: Pixel;
    //        if (line.length > 1) {
    //            this.lineString.setCoordinates(line.map(p => p));
    //            pixel = this.lineString.getClosestPoint(pixel);
    //        }
    //        else
    //            pixel = line[0];

    //        return pixel;
    //    }
};
